import { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Send, Cloud, Sparkles, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import type { ActivitySlideProps } from '../types';
import { stripHtml } from '../../../../utils/slides/stripHtml';

const WORD_COLORS = [
  'text-blue-500', 'text-purple-500', 'text-pink-500', 'text-emerald-500',
  'text-orange-500', 'text-cyan-500', 'text-rose-500', 'text-indigo-500',
];

interface Word {
  id: string;
  text: string;
  count: number;
  author: string;
}

export default function WordCloud({ slide, className = '' }: ActivitySlideProps) {
  const [inputWord, setInputWord] = useState('');
  const [words, setWords] = useState<Word[]>([
    { id: '1', text: 'Sunlight', count: 12, author: 'Multiple' },
    { id: '2', text: 'Oxygen', count: 10, author: 'Multiple' },
    { id: '3', text: 'Carbon', count: 8, author: 'Multiple' },
    { id: '4', text: 'Chlorophyll', count: 15, author: 'Multiple' },
    { id: '5', text: 'Energy', count: 9, author: 'Multiple' },
    { id: '6', text: 'Glucose', count: 7, author: 'Multiple' },
    { id: '7', text: 'Water', count: 11, author: 'Multiple' },
    { id: '8', text: 'Leaves', count: 6, author: 'Multiple' },
    { id: '9', text: 'Plants', count: 13, author: 'Multiple' },
    { id: '10', text: 'Process', count: 5, author: 'Multiple' },
  ]);

  const submitWord = () => {
    if (!inputWord.trim()) return;

    const existingWord = words.find(
      w => w.text.toLowerCase() === inputWord.toLowerCase()
    );

    if (existingWord) {
      setWords(words.map(w => 
        w.id === existingWord.id 
          ? { ...w, count: w.count + 1 }
          : w
      ));
    } else {
      setWords([...words, {
        id: Date.now().toString(),
        text: inputWord,
        count: 1,
        author: 'You',
      }]);
    }

    setInputWord('');
  };

  const maxCount = Math.max(...words.map(w => w.count));
  const minCount = Math.min(...words.map(w => w.count));

  const getWordSize = (count: number) => {
    const scale = (count - minCount) / (maxCount - minCount);
    return 16 + scale * 48; // Font size between 16px and 64px
  };

  const getWordColor = (count: number, index: number) => {
    return WORD_COLORS[index % WORD_COLORS.length];
  };

  return (
    <div className={`w-full ${className || ''}`}>
      <div className="bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-3xl p-5 sm:p-6 border border-border/40">
        {/* Header */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-600 px-4 py-1.5 rounded-full mb-3">
            <Cloud className="w-4 h-4" />
            <span className="text-sm font-bold">Word Cloud</span>
          </div>
          {slide.content?.showTitle !== false && slide.content?.title && slide.content.title !== 'Word Cloud' && (
            <h2 className="text-lg font-bold mb-1">{stripHtml(slide.content.title)}</h2>
          )}
          <p className="text-sm text-muted-foreground">
            {stripHtml(slide.content?.prompt || slide.content?.question || 'Share your thoughts')}
          </p>
        </div>

        {/* Input Section */}
        <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40 mb-5 max-w-lg mx-auto">
          <div className="flex gap-2">
            <Input
              value={inputWord}
              onChange={(e) => setInputWord(e.target.value)}
              placeholder="Type a word..."
              className="text-sm rounded-xl border-border/40 focus:ring-2 focus:ring-primary/30"
              onKeyDown={(e) => e.key === 'Enter' && submitWord()}
              aria-label="Enter a word for the word cloud"
            />
            <Button onClick={submitWord} size="default" className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-5 shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            <Sparkles className="w-3 h-3 inline mr-1" />
            {words.reduce((sum, w) => sum + w.count, 0)} words from the class
          </p>
        </div>

        {/* Word Cloud Display */}
        <div className="bg-card/40 backdrop-blur-sm rounded-2xl p-6 sm:p-10 border border-border/30 min-h-[250px] sm:min-h-[350px] flex items-center justify-center overflow-hidden mb-5">
          <div className="flex flex-wrap gap-3 sm:gap-5 justify-center items-center">
            <AnimatePresence>
              {words.map((word, index) => (
                <motion.div
                  key={word.id}
                  initial={{ scale: 0, rotate: -15, opacity: 0 }}
                  animate={{ 
                    scale: 1, 
                    rotate: 0,
                    opacity: 1,
                    y: Math.sin(index * 0.7) * 12,
                  }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ 
                    delay: index * 0.04,
                    type: 'spring',
                    stiffness: 200,
                    y: {
                      duration: 2.5 + (index % 3) * 0.5,
                      repeat: Infinity,
                      repeatType: 'reverse',
                      ease: 'easeInOut',
                    }
                  }}
                  whileHover={{ scale: 1.15, rotate: Math.random() > 0.5 ? 5 : -5 }}
                  className={`font-extrabold cursor-default select-none transition-all ${getWordColor(word.count, index)}`}
                  style={{
                    fontSize: `${Math.max(14, getWordSize(word.count) * 0.65)}px`,
                    lineHeight: 1.1,
                  }}
                >
                  {word.text}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* Top Words */}
        <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40">
          <h3 className="text-sm font-bold text-muted-foreground flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-yellow-500" />
            Most Popular Words
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[...words]
              .sort((a, b) => b.count - a.count)
              .slice(0, 6)
              .map((word, index) => (
                <motion.div
                  key={word.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06 }}
                  className="flex items-center gap-2 p-2.5 bg-background rounded-xl border border-border/30"
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-xs ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-700' : 'bg-muted-foreground/30'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{word.text}</p>
                    <p className="text-[10px] text-muted-foreground">{word.count}x</p>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}